import { PremiumBackground } from '../effects/PremiumBackground';
import React from 'react';
import { 
  Shield, 
  Lock, 
  FileCheck2, 
  Server, 
  ArrowRight, 
  ArrowDown, 
  CheckCircle2, 
  KeyRound, 
  Database,
  Search,
  Wrench,
  TrendingUp,
  Landmark
} from 'lucide-react';
import { useSessionStore } from '../../store/useSessionStore';
export interface ConfidentialityGatewayProps {
  id?: string;
}

export const ConfidentialityGateway: React.FC<ConfidentialityGatewayProps> = ({ id = 'confidentiality' }) => {
  const { activeGateStep, setActiveGateStep } = useSessionStore();

  const securityGateways = [
    {
      step: 1,
      tag: 'ШЛЮЗ № 1 // BLIND TEASER',
      title: 'Ступень 1: Квалификация инвестора (Слепой анонимный тизер)',
      headerShort: 'СЛЕПОЙ ТИЗЕР',
      subShort: '(Blind Teaser)',
      icon: Shield,
      whatHappens:
        'Мы формируем обезличенный профиль клиники (Blind Teaser): без названия, без точного адреса, без фото интерьеров. Раскрываются только общие финансовые параметры (выручка, EBITDA, округ/район, количество кресел).',
      result:
        'Отсекаем 80% праздных зевак, перекупщиков и конкурентов, пытающихся выведать информацию.',
      securityLevel: 'Уровень доступа: Публичный анонимный'
    },
    {
      step: 2,
      tag: 'ШЛЮЗ № 2 // STRICT NDA',
      title: 'Ступень 2: Коммерческая тайна (Соглашение о неразглашении — Strict NDA)',
      headerShort: 'СТРОГИЙ NDA',
      subShort: '(Штраф 5 млн ₽)',
      icon: Lock,
      whatHappens:
        'Потенциальный покупатель подтверждает наличие капитала и подписывает персональное соглашение о неразглашении конфиденциальной информации со штрафом от 5 000 000 рублей за каждый факт утечки.',
      result:
        'Только после этого раскрывается профиль актива и базовая финансовая модель. Ваши цифры находятся под судебной защитой.',
      securityLevel: 'Юридический штраф: от 5 000 000 ₽'
    },
    {
      step: 3,
      tag: 'ШЛЮЗ № 3 // TERM SHEET',
      title: 'Ступень 3: Предварительное соглашение (Term Sheet / Протокол о намерениях)',
      headerShort: 'TERM SHEET',
      subShort: '(Фиксация цены)',
      icon: FileCheck2,
      whatHappens:
        'Личный визит инвестора на объект организуется строго в нерабочие часы (под легендой технического аудита оборудования). Покупатель подписывает документ о намерениях (Term Sheet) с фиксацией индикативной цены бизнеса.',
      result:
        'Продавец застрахован от сбивания цены «на словах», а покупатель получает ограниченный эксклюзив на период финального оформления.',
      securityLevel: 'Режим визита: Строго нерабочие часы'
    },
    {
      step: 4,
      tag: 'ШЛЮЗ № 4 // DATA ROOM',
      title: 'Ступень 4: Изолированная комната данных (Virtual Data Room — VDR)',
      headerShort: 'КОМНАТА ДАННЫХ',
      subShort: '(VDR)',
      icon: Server,
      whatHappens:
        'Мы открываем электронный доступ юристам покупателя к изолированному серверу с договорами аренды, лицензиями и кадровыми документами.',
      result:
        'Финальное подтверждение юридической чистоты бизнеса и прямой выход на сделку у нотариуса (Closing).',
      securityLevel: 'Протокол: Изолированный VDR-шлюз'
    },
  ];

  const transactionPhases = [
    {
      number: 'ФАЗА 01',
      title: 'Фаза 1: Диагностика и фиксация рисков',
      subTitle: '(Assessment & Risk Freeze)',
      icon: Search,
      content:
        'Экспресс-аудит клиники по 604 контрольным точкам. Расчет индекса готовности (Deal Readiness Score™) и матрицы уязвимостей.',
      milestone:
        'Владелец держит в руках расчет потенциального дисконта и понимает, где лежат риски потери денег.'
    },
    {
      number: 'ФАЗА 02',
      title: 'Фаза 2: Реструктуризация и упаковка',
      subTitle: '(Remediation & Packaging)',
      icon: Wrench,
      content:
        'Сроки определяются выбранным сценарием (Light или Deep). Устранение критических дефектов: зачистка договоров по Постановлению № 736, перевод врачей в штат, нормализация прибыли (Add-backs), сборка Virtual Data Room.',
      milestone:
        'Актив переведен в статус полной инвестиционной пригодности. Выпущены анонимный тизер и инфо-меморандум.'
    },
    {
      number: 'ФАЗА 03',
      title: 'Фаза 3: Таргетированное предложение и конкурс инвесторов',
      subTitle: '(Bidding Dynamics)',
      icon: TrendingUp,
      content:
        'Точечный выход на профильных покупателей (федеральные сети, фонды, стратеги со своей инфраструктурой). Защита финансовой модели перед инвест-комитетами.',
      milestone:
        'Получение нескольких необязывающих оферт (NBO) и организация закрытого конкурса предложений для удержания максимального мультипликатора (до 7.5x).'
    },
    {
      number: 'ФАЗА 04',
      title: 'Фаза 4: Контрактная защита и расчеты',
      subTitle: '(SPA, Escrow & Closing)',
      icon: Landmark,
      content:
        'Юридическое конструирование обязывающего договора купли-продажи долей (SPA), утверждение формулы чистого долга, открытие эскроу-счета в банке и нотариальное подписание.',
      milestone:
        'Безопасный переход прав на доли ООО и перечисление денег на расчетный счет продавца с полным снятием личной субсидиарной ответственности.'
    },
  ];

  return (
    <section
      id={id}
      aria-label="Технология Шлюзов и вехи транзакции"
      className="relative w-full bg-white text-[#141416] py-28 md:py-36 border-t border-b border-[#EAE5DE] font-sans overflow-hidden"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={2} />

      
      

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, 2. ЗАГОЛОВОК H2, 3. ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="max-w-4xl mb-16 flex flex-col gap-6">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 11 // ПРОТОКОЛ БЕЗОПАСНОСТИ // СИСТЕМА ШЛЮЗОВ
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-tight leading-[1.1] text-[#141416]">
            Технология «Шлюзов» и вехи транзакции: <br />
            <span className="text-[#64748B] italic font-serif">как мы защищаем тайну сделки.</span>
          </h2>

          <div className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal space-y-3 max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            <p>
              Ваши сотрудники узнают о смене владельца последними — при представлении нового генерального директора.
            </p>
            <p className="text-[#141416] font-bold">
              Ваши конкуренты не узнают ничего. Никаких досок объявлений, спама в Telegram-каналах и публичных баз.
            </p>
          </div>
        </div>

        {/* =========================================================================
            ГОРИЗОНТАЛЬНЫЙ ЦЕПОЧЕЧНЫЙ КОННЕКТОР (ASCII СХЕМА ШЛЮЗОВ)
            ========================================================================= */}
        <div className="hidden lg:grid grid-cols-4 gap-4 mb-8">
          {securityGateways.map((g, idx) => {
            const isSelected = activeGateStep === g.step;
            return (
              <div 
                key={g.step}
                onClick={() => setActiveGateStep(g.step)}
                className={`p-4 border rounded-[2px] transition-all cursor-pointer flex items-center justify-between gap-3 shadow-xs ${
                  isSelected 
                    ? 'border-[#8C6D37] bg-[#8C6D37]/10 text-[#8C6D37]' 
                    : 'border-[#EAE5DE] bg-[#FAF8F5] text-[#141416] hover:border-[#CBD5E1]'
                }`}
              >
                <div>
                  <span className="font-mono text-[10px] text-[#64748B] block font-bold uppercase tracking-wider">
                    ШЛЮЗ № {g.step}
                  </span>
                  <span className="font-bold text-[13px] tracking-tight block text-[#141416]">
                    {g.headerShort}
                  </span>
                  <span className="text-[11px] text-[#64748B] font-mono">
                    {g.subShort}
                  </span>
                </div>
                {idx < 3 ? (
                  <ArrowRight className="w-4 h-4 text-[#64748B] shrink-0" />
                ) : (
                  <KeyRound className="w-4 h-4 text-[#8C6D37] shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* =========================================================================
            4. СИСТЕМА 4-Х УРОВНЕЙ ДОПУСКА (SECURITY GATEWAYS CARDS)
            ========================================================================= */}
        <div className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityGateways.map((g) => {
              const IconComp = g.icon;
              const isSelected = activeGateStep === g.step;

              return (
                <div
                  key={g.step}
                  onClick={() => setActiveGateStep(g.step)}
                  className={`bg-white border rounded-[2px] p-6 sm:p-7 flex flex-col justify-between transition-all duration-300 shadow-xs cursor-pointer group ${
                    isSelected
                      ? 'border-[#8C6D37] ring-1 ring-[#8C6D37] shadow-sm'
                      : 'border-[#EAE5DE] hover:border-[#8C6D37]/50'
                  }`}
                >
                  <div>
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-[#EAE5DE]">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-[2px] flex items-center justify-center transition-colors ${
                          isSelected 
                            ? 'bg-[#8C6D37] text-white' 
                            : 'bg-[#FAF8F5] border border-[#EAE5DE] text-[#8C6D37] group-hover:bg-[#8C6D37]/10'
                        }`}>
                          <IconComp className="w-4 h-4" />
                        </div>
                        <span className="font-mono text-[10px] text-[#8C6D37] font-bold uppercase tracking-wider">
                          {g.tag}
                        </span>
                      </div>
                      <span className="font-mono text-xs text-[#64748B] font-bold">
                        0{g.step}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-[15px] font-bold text-[#141416] leading-snug mb-4">
                      {g.title}
                    </h3>

                    {/* Content: Что происходит */}
                    <div className="space-y-4 text-xs leading-relaxed text-[#334155]">
                      <div>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#141416] font-bold block mb-1">
                          Что происходит:
                        </span>
                        <p className="text-[#334155] font-normal leading-relaxed">
                          {g.whatHappens}
                        </p>
                      </div>

                      {/* Content: Результат */}
                      <div className="p-3 bg-[#FAF8F5] border-l-2 border-[#8C6D37] rounded-r-[2px] shadow-xs">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#8C6D37] font-bold block mb-1">
                          Результат:
                        </span>
                        <p className="text-[#141416] font-bold leading-relaxed">
                          {g.result}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Footer telemetry */}
                  <div className="mt-6 pt-4 border-t border-[#EAE5DE] flex items-center gap-2 text-[11px] text-[#64748B] font-mono">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#8C6D37] shrink-0" />
                    <span className="font-bold">{g.securityLevel}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* =========================================================================
            5. ДОРОЖНАЯ КАРТА ТРАНЗАКЦИИ: 4 УПРАВЛЯЕМЫЕ ФАЗЫ (MILESTONE TIMELINE)
            ========================================================================= */}
        <div className="mb-20">
          <div className="max-w-4xl mb-10 flex flex-col gap-4">
            <span className="luxury-eyebrow text-[#8C6D37] block font-bold tracking-wider uppercase">
              УПРАВЛЯЕМЫЙ ГРАФИК ГОТОВНОСТИ
            </span>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-[#141416] tracking-tight">
              Дорожная карта транзакции: 4 управляемые фазы
            </h3>
            <div className="text-[15px] text-[#334155] leading-relaxed font-normal bg-[#FAF8F5] border border-[#EAE5DE] p-6 rounded-[2px] shadow-xs">
              <p className="mb-3">
                В профессиональном M&A не существует фиксированных сроков вроде «продадим ровно за 120 дней». 
                Скорость сделки зависит от выбранной вами стратегии: от 4–6 недель при быстрой продаже «как есть» до нескольких месяцев при капитальной реструктуризации по Налоговой амнистии 176-ФЗ. 
              </p>
              <strong className="text-[#141416] font-bold block">
                Мы управляем процессом по контрольным вехам готовности (Milestones):
              </strong>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {transactionPhases.map((phase, idx) => {
              const IconComp = phase.icon;
              return (
                <div
                  key={idx}
                  className="bg-white border border-[#EAE5DE] rounded-[2px] p-6 sm:p-8 flex flex-col justify-between shadow-xs hover:border-[#CBD5E1] transition-colors"
                >
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-[#EAE5DE]">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-[2px] bg-[#FAF8F5] border border-[#EAE5DE] flex items-center justify-center text-[#8C6D37]">
                          <IconComp className="w-4 h-4 text-[#8C6D37]" />
                        </div>
                        <div>
                          <span className="font-mono text-[11px] text-[#8C6D37] font-bold tracking-wider uppercase">
                            {phase.number}
                          </span>
                          <span className="text-[12px] text-[#64748B] block font-mono font-bold">
                            {phase.subTitle}
                          </span>
                        </div>
                      </div>
                    </div>

                    <h4 className="text-base sm:text-lg font-bold text-[#141416] mb-3">
                      {phase.title}
                    </h4>

                    <div className="text-[14px] text-[#334155] leading-relaxed mb-6 font-normal">
                      <span className="font-mono text-[11px] uppercase tracking-wider text-[#141416] font-bold block mb-1">
                        Содержание:
                      </span>
                      <p>{phase.content}</p>
                    </div>
                  </div>

                  {/* Контрольная точка (Milestone) */}
                  <div className="p-4 bg-[#FAF8F5] border-l-2 border-[#8C6D37] rounded-r-[2px] text-[13px] shadow-xs">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
                      <span className="font-mono text-[11px] font-bold text-[#8C6D37] uppercase tracking-wider">
                        Контрольная точка (Milestone):
                      </span>
                    </div>
                    <p className="text-[#141416] font-bold leading-relaxed">
                      {phase.milestone}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* =========================================================================
            6. ПРОТОКОЛ АБСОЛЮТНОЙ ИЗОЛЯЦИИ ДАННЫХ (CALLOUT BOX)
            ========================================================================= */}
        <div className="bg-[#8C6D37]/10 border-l-4 border-[#8C6D37] border-y border-r border-[#8C6D37]/40 rounded-[2px] p-6 sm:p-8 mb-16 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#8C6D37]" />
          <div className="flex items-start gap-4 pt-2">
            <div className="w-10 h-10 rounded-[2px] bg-white border border-[#8C6D37]/40 flex items-center justify-center text-[#8C6D37] shrink-0 mt-0.5 shadow-xs">
              <Database className="w-5 h-5" />
            </div>
            <div className="space-y-2">
              <h4 className="text-base sm:text-lg font-bold text-[#141416] flex items-center gap-2">
                <span>🔒 Регламент Air-Gapped Security (Работа с базами 1С и МИС):</span>
              </h4>
              <p className="text-[15px] text-[#334155] leading-relaxed font-normal">
                При проведении аудита мы не подключаем внешние сервисы к вашей живой рабочей программе.
              </p>
              <p className="text-[15px] text-[#141416] font-bold leading-relaxed">
                Вы передаете обезличенные копии баз данных (.dt) без персональных данных пациентов (деперсонификация по 152-ФЗ). 
                Анализ проводится на изолированных серверах бутика. По завершении проекта данные уничтожаются с предоставлением электронного криптографического акта.
              </p>
            </div>
          </div>
        </div>

        {/* =========================================================================
            7. НАВИГАТОР ПЕРЕХОДА К СЛЕДУЮЩЕМУ ЭКРАНУ
            ========================================================================= */}
        <div className="text-center pt-2">
          <a
            href="#syndicate"
            className="group inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 hover:bg-[#FAF8F5] border border-transparent hover:border-[#EAE5DE] rounded-[2px]"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 12: МЕЖДИСЦИПЛИНАРНЫЙ ШТАБ S&P ↓ ]</span>
          </a>
        </div>

      </div>
    </section>
  );
};
